import React, { createContext, useContext, useState } from "react";
import { useAdminAgents } from "../hooks/useAdminAgents";
import { IAgent, IAgentBlueprint } from "../types/base";
import { Menu } from "../types/menu";

interface AgentEditorContextType {
  selectedAgent: IAgent | null;
  localAgentBlueprint: IAgentBlueprint | null;
  setSelectedAgent: (agent: IAgent | null) => void;
  setLocalAgentBlueprint: (agent: IAgentBlueprint | null) => void;
  fetchAgent: (id: string) => Promise<IAgent | null>;
  agents: IAgent[];
  isAgentsLoading: boolean;
  agentsError: Error | null;
  refetchAgents: () => void;
  menuData: Menu | null;
  setMenuData: (data: Menu | null) => void;
}

const AgentEditorContext = createContext<AgentEditorContextType | undefined>(
  undefined,
);

export function AgentEditorProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [selectedAgent, setSelectedAgent] = useState<IAgent | null>(null);
  const [localAgentBlueprint, setLocalAgentBlueprint] =
    useState<IAgentBlueprint | null>(null);

  const [menuData, setMenuData] = useState<Menu | null>(null);

  const {
    data: agents = [],
    isLoading: isAgentsLoading,
    error: agentsError,
    refetch: refetchAgents,
  } = useAdminAgents();

  const contextValue: AgentEditorContextType = {
    selectedAgent,
    localAgentBlueprint,
    setSelectedAgent,
    setLocalAgentBlueprint,
    fetchAgent: async (id: string) => {
      try {
        let foundAgent = agents.find((agent) => agent.id === id) ?? null;
        if (!foundAgent) {
          const result = await refetchAgents();
          const freshAgents = result.data || [];
          foundAgent = freshAgents.find((agent) => agent.id === id) ?? null;
        }
        return foundAgent;
      } catch (error) {
        console.error("Error fetching agent....:", error);
        throw error;
      }
    },
    agents,
    isAgentsLoading,
    agentsError,
    refetchAgents,
    menuData,
    setMenuData,
  };

  return (
    <AgentEditorContext.Provider value={contextValue}>
      {children}
    </AgentEditorContext.Provider>
  );
}

export const useAgentEditor = () => {
  const context = useContext(AgentEditorContext);
  if (context === undefined) {
    throw new Error(
      "useAgentEditor must be used within an AgentEditorProvider",
    );
  }
  return context;
};
